import CrudPage from "@/components/CrudPage";

const statusTag = (s) =>
  s === "online" ? <span className="sbc-tag tag-online"><span className="led led-green" /> conectado</span>
    : s === "degraded" ? <span className="sbc-tag tag-warn"><span className="led led-amber" /> parcial</span>
    : <span className="sbc-tag tag-offline"><span className="led led-gray" /> offline</span>;

const loginTag = (r) => {
  if (!r.registration_required) return <span className="sbc-tag tag-online">sem login</span>;
  if (r.registration_status === "registered") return <span className="sbc-tag tag-online">registrado</span>;
  if (r.registration_status === "trying") return <span className="sbc-tag tag-warn">registrando</span>;
  return <span className="sbc-tag tag-offline">login falhou</span>;
};

const CHANNEL_OPTIONS = [
  { value: 30, label: "30 canais simultaneos" },
  { value: 60, label: "60 canais simultaneos" },
  { value: 120, label: "120 canais simultaneos" },
  { value: 240, label: "240 canais simultaneos" },
];

const loadOnlyOperatorTrunks = (rows) =>
  (rows || []).filter((row) => String(row?.tipo_tronco || "operadora").toLowerCase() !== "cliente");

export default function SipTrunks() {
  return (
    <CrudPage
      title="SIP Trunk"
      subtitle="Interconexao SIP com operadoras"
      endpoint="/operadoras"
      testidPrefix="sip-trunks"
      loadTransform={loadOnlyOperatorTrunks}
      canEdit={(row) => !row?.read_only}
      canDelete={(row) => !row?.read_only}
      headers={["Nome", "Autenticacao", "Direcao", "Destino", "Proto", "Usuario", "Canais", "CPS", "Conectividade", "Login"]}
      transformSubmit={(form) => ({
        ...form,
        tipo_tronco: "operadora",
        canais_max: Number(form.canais_max || 30),
        porta: Number(form.porta || 5060),
        cps_limite: Number(form.cps_limite || 10),
        qualify_seconds: Number(form.qualify_seconds || 30),
        ips_autorizados: String(form.ips_autorizados || "")
          .split(/\r?\n|,|;/)
          .map((value) => value.trim())
          .filter(Boolean),
      })}
      renderRow={(r) => (
        <>
          <td>
            <div className="font-medium">{r.nome}</div>
            <div className="text-[10px] text-[color:var(--text-muted)]">{r.descricao || "Sem descricao"}</div>
          </td>
          <td className="uppercase text-xs">{r.modo_autenticacao || (r.usuario ? "registro" : "ip")}</td>
          <td className="uppercase text-xs">{r.direcao || "ambos"}</td>
          <td className="font-mono text-xs">
            <div>{r.host}</div>
            <div className="text-[10px] text-[color:var(--text-muted)]">{r.dominio_sip || "-"}</div>
          </td>
          <td className="font-mono uppercase text-[color:var(--text-secondary)]">{r.protocolo}</td>
          <td className="font-mono">{r.usuario || "-"}</td>
          <td className="font-mono">{r.canais_max}</td>
          <td className="font-mono">{r.cps_limite || "-"}</td>
          <td>{r.enabled ? statusTag(r.status || "offline") : <span className="sbc-tag tag-offline">desativado</span>}</td>
          <td>{r.enabled ? loginTag(r) : <span className="sbc-tag tag-offline">desativado</span>}</td>
        </>
      )}
      defaultValues={{
        tipo_tronco: "operadora",
        nome: "",
        descricao: "",
        host: "",
        porta: 5060,
        protocolo: "udp",
        modo_autenticacao: "ip",
        direcao: "ambos",
        usuario: "",
        senha: "",
        dominio_sip: "",
        codec: "PCMA,PCMU,G729",
        canais_max: 30,
        cps_limite: 10,
        qualify_seconds: 30,
        prefixo: "",
        ips_autorizados: "",
        recording_enabled: false,
        enabled: true,
      }}
      fields={[
        { key: "nome", label: "Nome", placeholder: "Ex: SIP Trunk Operadora A", full: true },
        { key: "descricao", label: "Descricao", placeholder: "Ex: Interconexao com a operadora principal", full: true },
        {
          key: "modo_autenticacao",
          label: "Modo de autenticacao",
          type: "select",
          options: [
            { value: "ip", label: "Por IP" },
            { value: "registro", label: "Por usuario e senha" },
            { value: "hibrido", label: "IP + usuario e senha" },
          ],
        },
        {
          key: "direcao",
          label: "Direcao",
          type: "select",
          options: [
            { value: "ambos", label: "Entrada e saida" },
            { value: "entrada", label: "Somente entrada" },
            { value: "saida", label: "Somente saida" },
          ],
        },
        { key: "host", label: "Host / IP principal", mono: true, placeholder: "sip.operadora.com.br ou 200.150.10.5" },
        { key: "dominio_sip", label: "Dominio SIP", mono: true, placeholder: "opcional" },
        { key: "porta", label: "Porta SIP", type: "number", mono: true },
        {
          key: "protocolo",
          label: "Protocolo",
          type: "select",
          options: [
            { value: "udp", label: "UDP" },
            { value: "tcp", label: "TCP" },
            { value: "tls", label: "TLS" },
          ],
        },
        {
          key: "usuario",
          label: "Usuario SIP",
          mono: true,
          placeholder: "obrigatorio se usar registro",
          hidden: (form) => form.modo_autenticacao === "ip",
        },
        {
          key: "senha",
          label: "Senha SIP",
          mono: true,
          placeholder: "obrigatoria se usar registro",
          hidden: (form) => form.modo_autenticacao === "ip",
        },
        { key: "codec", label: "Codecs (ordem)", mono: true, hint: "Ex.: PCMA,PCMU,G729", full: true },
        {
          key: "canais_max",
          label: "Canais simultaneos",
          type: "select",
          options: CHANNEL_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
          hint: "Quantidade maxima prevista para a operadora.",
        },
        { key: "cps_limite", label: "CPS", type: "number", mono: true, hint: "Chamadas por segundo permitidas." },
        { key: "qualify_seconds", label: "Qualify (s)", type: "number", mono: true, hint: "Frequencia de verificacao do trunk." },
        { key: "prefixo", label: "Prefixo opcional", mono: true, placeholder: "Ex.: 0011" },
        {
          key: "ips_autorizados",
          label: "IPs autorizados",
          type: "textarea",
          full: true,
          rows: 4,
          placeholder: "Um IP por linha ou separados por virgula",
          hint: "Use quando o trunk for autenticado por IP.",
        },
        { key: "recording_enabled", label: "Gravar chamadas do trunk", type: "toggle" },
        { key: "enabled", label: "Ativado", type: "toggle" },
      ]}
    />
  );
}
